/**
 * Hook para monitorar inboxes desconectadas ou com erro
 * 
 * ARQUITETURA:
 * - Usa React Query para cache e estado
 * - Realtime centralizado em useInboxPageRealtime.ts (invalida ['disconnected-inboxes'])
 * - Sem polling e sem subscription local — evita colisão de canais
 *   quando múltiplas instâncias do componente são montadas simultaneamente
 * 
 * @param tenantId - ID do tenant para filtrar as inboxes
 * @returns Query com lista de inboxes problemáticas
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para este hook
const logger = createLogger('useDisconnectedInboxes');

// Interface para inbox com problema de conexão
// v1.0.2: Adicionado 'connecting' ao status
export interface DisconnectedInbox {
  id: string;
  name: string;
  status: 'disconnected' | 'error' | 'connecting';
  channel_type: string;
  provider_name: string | null;
  provider_slug: string | null;
}

/**
 * Busca inboxes com status 'disconnected' ou 'error' do tenant atual
 * Realtime centralizado em useInboxPageRealtime.ts
 * 
 * @param tenantId - ID do tenant para filtrar as inboxes
 * @returns Query com lista de inboxes problemáticas
 */
export function useDisconnectedInboxes(tenantId: string | undefined) {
  // Realtime removido deste hook - centralizado em useInboxPageRealtime.ts
  // O React Query compartilha cache pela queryKey, então invalidateQueries
  // disparado pelo hook centralizado atualiza todas as instâncias deste hook.

  const query = useQuery({
    queryKey: ['disconnected-inboxes', tenantId],
    queryFn: async (): Promise<DisconnectedInbox[]> => {
      if (!tenantId) return [];

      logger.debug('Buscando inboxes desconectadas via RPC', { tenantId });

      const { data, error } = await supabase.rpc('get_user_disconnected_inboxes', {
        p_tenant_hint: tenantId
      });

      if (error) {
        logger.error('Erro ao buscar inboxes desconectadas', { error });
        throw error;
      }

      // v1.0.2: Inclui 'connecting' no mapeamento de status
      const result = (data || []).map((inbox: any) => ({
        id: inbox.id,
        name: inbox.name,
        status: inbox.status as 'disconnected' | 'error' | 'connecting',
        channel_type: inbox.channel_type,
        provider_name: inbox.provider_name || null,
        provider_slug: inbox.provider_slug || null,
      }));

      logger.debug('Inboxes desconectadas encontradas (com permissão)', { count: result.length });
      return result;
    },
    enabled: !!tenantId,
    // Sem polling - Realtime centralizado em useInboxPageRealtime.ts
  });

  return query;
}
